import type { FetchClient, RequestOptions } from '../../../lib/http/http.types';
import { ApiError } from '../../../lib/http/ApiError';
import { toQueryString } from '../../../lib/url/urlUtils';
import type {
  RightsProfile,
  CatalogueLine,
  CreateRightsProfilePayload,
  PaginatedRightsProfilesResponse,
  RightsProfilesQueryParams,
} from '../types/rights-profile.types';
import RIGHTS_PROFILES_ENDPOINTS from './rights-profiles.endpoints';
import {
  ProfileInUseError,
  parseAssignedCount,
  parseProfileName,
} from '../errors/profile-in-use.error';
import { classifyConflict } from '../errors/profile-conflict.error';
import { hydratePermissionMatrix, matrixToPayload } from '../logic/permissionMatrix.logic';

export interface RightsProfilesApi {
  /** One page of the organization's profiles, filtered on the name and sorted (HOMCO-1413). */
  getAll(
    params?: RightsProfilesQueryParams,
    opts?: RequestOptions,
  ): Promise<PaginatedRightsProfilesResponse>;
  getCatalogue(opts?: RequestOptions): Promise<CatalogueLine[]>;
  getById(id: string, opts?: RequestOptions): Promise<RightsProfile>;
  create(payload: CreateRightsProfilePayload, opts?: RequestOptions): Promise<RightsProfile>;
  update(
    id: string,
    payload: Partial<CreateRightsProfilePayload>,
    opts?: RequestOptions,
  ): Promise<RightsProfile>;
  /** Duplicates a profile as "[name] (copie)" with the same effective rights. */
  duplicate(id: string, opts?: RequestOptions): Promise<RightsProfile>;
  remove(id: string, opts?: RequestOptions): Promise<void>;
}

/**
 * Translates a write-path 409 (`POST`/`PATCH`/`DELETE`) into a typed conflict error.
 * A locked profile and a name conflict are recognised first (same for every write);
 * on a `DELETE`, a remaining 409 is the "profile in use" case. Anything else
 * propagates unchanged.
 */
function throwTranslatedConflict(error: unknown, isDelete: boolean): never {
  if (ApiError.isApiError(error) && error.statusCode === 409) {
    const message = error.messages[0];
    const conflict = classifyConflict(message);
    if (conflict) throw conflict;
    if (isDelete) {
      throw new ProfileInUseError(parseAssignedCount(message), parseProfileName(message));
    }
  }
  throw error;
}

/**
 * Rights-profiles CRUD client. The API is snake_case and matches the frontend
 * types 1:1 (`RightsProfileResponseDto`, `PermissionDto`), so responses are typed
 * passthroughs — no row mapper is needed.
 *
 * Write methods translate the backend's 409 conflicts into typed errors so callers
 * switch on the error type instead of a raw HTTP status: a locked profile
 * (`ProfileLockedError`), a name conflict (`ProfileNameConflictError`, on
 * create/update/duplicate) and a still-assigned profile (`ProfileInUseError`, on
 * delete). The backend returns all three as 409 with distinct messages and no
 * structured discriminator, so classification reads the message keyword only.
 * Every other error propagates unchanged.
 */
/** « [name] (copie) », trimmed so the result never exceeds the backend's 100-char cap. */
const COPY_SUFFIX = ' (copie)';
const NAME_MAX = 100;
function buildCopyName(name: string): string {
  const base = name.slice(0, NAME_MAX - COPY_SUFFIX.length);
  return `${base}${COPY_SUFFIX}`;
}

export function RightsProfilesApiFactory(client: FetchClient): RightsProfilesApi {
  const create: RightsProfilesApi['create'] = async (payload, opts) => {
    try {
      return await client.post<RightsProfile>(RIGHTS_PROFILES_ENDPOINTS.BASE, payload, opts);
    } catch (error) {
      throwTranslatedConflict(error, false);
    }
  };

  return {
    getAll: (params, opts) => {
      const qs = new URLSearchParams();
      if (params?.page != null) qs.set('page', String(params.page));
      if (params?.limit != null) qs.set('limit', String(params.limit));
      if (params?.sortBy != null) qs.set('sortBy', params.sortBy);
      if (params?.sortOrder != null) qs.set('sortOrder', params.sortOrder);
      const name = params?.name?.trim();
      if (name) qs.set('name[like]', name);
      const query = toQueryString(qs);
      return client.get<PaginatedRightsProfilesResponse>(
        `${RIGHTS_PROFILES_ENDPOINTS.BASE}${query ? `?${query}` : ''}`,
        opts,
      );
    },

    getCatalogue: opts => client.get<CatalogueLine[]>(RIGHTS_PROFILES_ENDPOINTS.CATALOGUE, opts),

    getById: (id, opts) => client.get<RightsProfile>(RIGHTS_PROFILES_ENDPOINTS.BY_ID(id), opts),

    create,

    update: async (id, payload, opts) => {
      try {
        return await client.patch<RightsProfile>(
          RIGHTS_PROFILES_ENDPOINTS.BY_ID(id),
          payload,
          opts,
        );
      } catch (error) {
        throwTranslatedConflict(error, false);
      }
    },

    duplicate: async (id, opts) => {
      // Send the FULL matrix (every applicable action, enabled or not), not just the
      // granted ones: an empty payload makes the backend apply display-only defaults
      // (rights-profiles.service.ts), so a zero-right source would otherwise be
      // granted Display everywhere. Explicit disabled rows keep the copy identical.
      const [source, catalogue] = await Promise.all([
        client.get<RightsProfile>(RIGHTS_PROFILES_ENDPOINTS.BY_ID(id), opts),
        client.get<CatalogueLine[]>(RIGHTS_PROFILES_ENDPOINTS.CATALOGUE, opts),
      ]);
      const matrix = hydratePermissionMatrix(catalogue, source.permissions);
      return create(
        {
          // Keep the copy name within the backend's 100-char limit (else a 400):
          // trim the base so that base + " (copie)" never exceeds it.
          name: buildCopyName(source.name),
          description: source.description,
          permissions: matrixToPayload(catalogue, matrix),
        },
        opts,
      );
    },

    remove: async (id, opts) => {
      try {
        await client.delete(RIGHTS_PROFILES_ENDPOINTS.BY_ID(id), opts);
      } catch (error) {
        throwTranslatedConflict(error, true);
      }
    },
  };
}
